import { inject, observer } from 'mobx-react';
import React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import { Instance } from 'mobx-state-tree';
import { TLCode } from '../../common/enums';
import styled from '@emotion/styled';
import {
  ApplicationStore,
  PharmacyStore,
  SecretariatNeighborStore,
  GroupStore,
  UiStore,
  AnchorStore,
  AuthenticationStore,
  SecretariatStore,
} from '../../store';
import PharmaciesMap from './PharmaciesMap';
import PharmaciesList from './PharmaciesList';
import { quickFilterFormatter } from '../../common/quickFilterFormatterUtils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

interface PharmaciesPageProps {
  PharmacyStore?: Instance<typeof PharmacyStore>;
  SecretariatNeighborStore?: Instance<typeof SecretariatNeighborStore>;
  GroupStore?: Instance<typeof GroupStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  UiStore?: Instance<typeof UiStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface PharmaciesPageState {
  isLoading: boolean;
  filter: any;
  view: 'map' | 'grid';
  activeFilter: 'active' | 'inactive' | 'both';
  levelFilter: 'zero' | 'one' | 'three' | 'all';
  groupFilter: 'all';
}

@inject('PharmacyStore', 'SecretariatNeighborStore', 'GroupStore')
@inject('ApplicationStore')
@inject('UiStore')
@inject('AuthenticationStore')
@inject('SecretariatStore')
@observer
class PharmaciesPage extends React.Component<PharmaciesPageProps, PharmaciesPageState> {
  state = {
    isLoading: false,
    filter: '',
    view: 'grid',
    activeFilter: 'active',
    levelFilter: 'all',
    groupFilter: 'all',
  } as PharmaciesPageState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { PharmacyStore, GroupStore, SecretariatNeighborStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    if (!PharmacyStore!.isListLoaded())
      await Promise.all([PharmacyStore!.loadList(), GroupStore!.loadList(), SecretariatNeighborStore!.loadList()]);
    if (this.props.UiStore!.pharmaciesSecretariatIdFilter === 0)
      this.props.UiStore!.setPharmaciesSecretariatIdFilter(this.getSelectedSecretariatId());
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private getSelectedSecretariatId(): number | undefined {
    return this.props.AuthenticationStore!.isPharmacy &&
      this.props.SecretariatStore!.dataList.find(
        (s) =>
          s.secretariatId ===
          this.props.PharmacyStore!.dataList.find((p) => p.pharmacyId === this.props.AuthenticationStore!.pharmacyId)!
            .secretariatId,
      ) !== undefined
      ? this.props.PharmacyStore!.dataList.find((p) => p.pharmacyId === this.props.AuthenticationStore!.pharmacyId)!
          .secretariatId
      : this.props.AuthenticationStore!.isSecretariat && this.props.AuthenticationStore!.secretariatId
      ? this.props.AuthenticationStore!.secretariatId
      : this.props.SecretariatStore!.selectedSecretariatId;
  }

  loadPharmacyList = async () => {
    this._isMounted && this.setState({ isLoading: true });
    await this.props.PharmacyStore!.loadList();
    this._isMounted && this.setState({ isLoading: false });
  };

  quickFilterFormatter = (s) => {
    return quickFilterFormatter(s);
  };

  setFilterText = (filterParam) => {
    const filter = filterParam; //this.quickFilterFormatter(filterParam);
    this.setState({
      filter,
    });
  };

  render() {
    const { view, isLoading, filter } = this.state;
    const { PharmacyStore, GroupStore, ApplicationStore, UiStore } = this.props;
    const pharmaciesList = PharmacyStore!.dataList;
    const groupList = GroupStore!.dataList;
    const activeFilter = UiStore!.pharmaciesActiveFilter;
    const levelFilter = UiStore!.pharmaciesLevelFilter;
    const groupFilter = UiStore!.pharmaciesGroupFilter;
    const textFilter = UiStore!.pharmaciesTextFilter;
    const secretariatIdFilter = UiStore!.pharmaciesSecretariatIdFilter;
    return (
      <MainSectionLayout isLoading={isLoading} title={TLCode.PHARMACIES_Title}>
        <Container>
          {view === 'grid' ? (
            <PharmaciesList
              deletePharmacy={PharmacyStore!.deleteElement}
              filter={textFilter}
              isLoading={isLoading}
              navigate={ApplicationStore!.navigate}
              pharmaciesList={pharmaciesList}
              setFilterText={this.setFilterText}
              showOnMap={() => this.setState({ view: 'map' })}
              activeFilter={activeFilter}
              levelFilter={levelFilter}
              groupFilter={groupFilter}
              secretariatFilter={secretariatIdFilter}
              setActiveFilter={(filter) => {
                this.setState({ activeFilter: filter });
                UiStore!.setPharmaciesActiveFilter(filter);
              }}
              setTextFilter={(filter) => {
                this.setState({ filter: filter });
                UiStore!.setPharmaciesTextFilter(filter);
              }}
              setLevelFilter={(filter) => {
                this.setState({ levelFilter: filter });
                UiStore!.setPharmaciesLevelFilter(filter);
              }}
              setGroupFilter={(filter) => {
                const filterU = filter.toUpperCase();
                this.setState({ groupFilter: filterU });
                UiStore!.setPharmaciesGroupFilter(filterU);
              }}
              setSecretariatFilter={(filter) => {
                UiStore!.setPharmaciesSecretariatIdFilter(filter);
              }}
              reloadPharmacyList={this.loadPharmacyList}
            />
          ) : (
            <PharmaciesMap
              isLoading={isLoading}
              onBackToList={() => this.setState({ view: 'grid' })}
              pharmaciesList={pharmaciesList}
              groupList={groupList}
            />
          )}
        </Container>
      </MainSectionLayout>
    );
  }
}

export default PharmaciesPage;
